import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import { withNamespaces } from "react-i18next"
import { toast } from "react-toastify";
import { Badge, Col, Label, ListGroup, ListGroupItem, Row } from "reactstrap";
import ComplianceFrameworkSettingsService from "src/modules/data-protection/apis/ComplianceFrameworkSettingsService";
import TaskControlsService from "src/modules/data-protection/apis/TaskControlsService";
import { ControlsFrameworks } from "src/modules/data-protection/constants";
import { useCallback, useState } from "react";
import { EMPTY_LIST } from "src/common/constants";
import AsyncReactSelect from "src/components/AsyncReactSelect";
import { formatControlNumber } from "src/modules/data-protection/helpers/Controls";

const ISMSControls = ({
    t,
    lng,
    taskId,
    defaultValues,
    controlsOptions,
    isReadOnly
}) => {
    const mappedDefaultValues = {};
    Object.keys(defaultValues || {}).forEach((frameworkName) => {
        mappedDefaultValues[frameworkName] = defaultValues[frameworkName].map((defauleSelected) => {
            return {
                value: defauleSelected.id,
                label: `${formatControlNumber(defauleSelected.control_number, frameworkName)}. ${t(defauleSelected.title)}`
            }
        })
    });

    const [expandedList, setExpandedList] = useState([]);
    const [selectedValues, setSelectedValues] = useState(mappedDefaultValues);

    const taskControlsService = TaskControlsService.getInstance();

    const ismsAccessibleControlsQuery = useQuery({
        queryKey: [
            'gdpr-fetch-isms-accessible-controls',
            controlsOptions
        ],
        queryFn: async () => {
            const service = ComplianceFrameworkSettingsService.getInstance();

            return await service.fetchAccessibleControls({
                ...controlsOptions
            });
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t('An error occurred while retrieving the controls list.'), {
                type: 'error'
            });
        }
    });

    const {
        mutate: handleRemoveControl
    } = useMutation({
        mutationFn: async ({
            control
        }) => {
            return await taskControlsService.removeControl(taskId, {
                control: control.value.toString()
            });
        },
        onSuccess: (_response, { control, framework }) => {
            toast(t("The control was successfully removed."), {
                type: "success"
            });

            setSelectedValues((prev) => {
                return {
                    ...prev,
                    [framework]: [...prev[framework]?.filter((item) => item.value !== control.value)]
                }
            });
        },
        onError: () => {
            toast(t("Failed to remove the control. Please try again."), {
                type: "error"
            });
        }
    });

    const {
        mutate: handleAddControl
    } = useMutation({
        mutationFn: async ({
            control
        }) => {
            return await taskControlsService.addControl(taskId, {
                control: control.value.toString()
            });
        },
        onSuccess: (_response, { framework, control }) => {
            toast(t("The selected control was successfully added."), {
                type: "success"
            });

            setSelectedValues((prev) => {
                return {
                    ...prev,
                    [framework]: [...(prev[framework] || []), {...control}]
                }
            });
        },
        onError: () => {
            toast(t("An error occurred while adding the control."), {
                type: "error"
            });
        }
    });

    const {
        accessible_frameworks = []
    } = ismsAccessibleControlsQuery.data || {};

    const controlsQueries = useQueries({
        queries: accessible_frameworks.map(framework => ({
            queryKey: [`gdpr-controls`, lng, framework],
            queryFn: async () => {
                const service = TaskControlsService.getInstance();

                const response = await service.fetchControls({
                    type: framework
                });

                return (response || []).map((control) => {
                    return {
                        value: control.id,
                        label: `${formatControlNumber(control.control_number, framework)}. ${t(control.title)}`,
                        baseLabel: control.title
                    }
                })
            },
            // enabled: ismsAccessibleControlsQuery.isFeched && ismsAccessibleControlsQuery.isSuccess,
            enabled: false
        }))
    });

    const controlsQueriesResults = accessible_frameworks.map((frameworkKey, index) => ({
        ...controlsQueries[index],
        frameworkName: frameworkKey
    }));

    const loadControlsOptions = useCallback(async (params) => {
        const {
            controlQuery
        } = params;

        if (!controlQuery) throw new Error();

        return {
            options: (await controlQuery.refetch())?.data || [],
            pageIndex: 1, 
            pageSize: 100,
            totalCount: 100
        };
    }, [ ]);

    if (ismsAccessibleControlsQuery.isFetching) {
        return (
            <Row>
                <Col sm="6">
                    <div
                        className="dt-field dt-skeleton dt-select-list"
                        style={{ marginBottom: 16 }}
                    ></div>
                </Col>

                <Col sm="6">
                    <div
                        className="dt-field dt-skeleton dt-select-list"
                        style={{ marginBottom: 16 }}
                    ></div>
                </Col>
            </Row>
        )
    }

    const toggleExpandedList = (frameworkKeyName) => {
        setExpandedList((prev) => {
            if (prev.findIndex((item) => frameworkKeyName === item) > -1) {
                return [...prev].filter((item) => item !== frameworkKeyName);
            }

            return [...prev, frameworkKeyName];
        })
    }

    return (
        <Row>
            {controlsQueriesResults.map((controlQuery) => {
                const controleFrameworkInfo = ControlsFrameworks.find((framework) => framework.key === controlQuery.frameworkName);

                const itemSelectedControls = selectedValues[controlQuery.frameworkName] || [];

                const panelExpanded = expandedList.includes(controlQuery.frameworkName);

                return (
                    <Col sm="12" md="6" className="mb-5">
                        <div>
                            <Label className="form-label text-dark d-flex justify-content-between">
                                {t(controleFrameworkInfo?.title)}
                                <span
                                    onClick={() => toggleExpandedList(controlQuery.frameworkName)}
                                    className="expand-btn btn-primary d-inline-block ml-auto mr-0 p-1"
                                    style={{
                                        borderRadius: '0.25rem',
                                        lineHeight: '20px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {panelExpanded ? t("Collapse list") : t("Expand list")}
                                </span>
                            </Label>

                            <div className="d-flex flex-column items-container">
                                <div
                                    style={{
                                        border: '1px solid #dadada',
                                        padding: '10px',
                                        borderRadius: '6px'
                                    }}      
                                >   
                                    {itemSelectedControls.length > 0 ? (
                                        <ListGroup
                                            type="inline"
                                            style={{
                                                maxHeight: panelExpanded ? "171px" : "51px",
                                                overflowY: 'scroll',
                                                transition: 'all .3s ease'
                                            }}
                                        >
                                            {itemSelectedControls.map((control, index) => {
                                                return (
                                                    <ListGroupItem key={index}>
                                                        {control.label}

                                                        {!isReadOnly && (
                                                            <Badge
                                                                className="delete-btn text-dark pt-2"
                                                                style={{
                                                                    float: 'right',
                                                                    cursor: 'pointer'
                                                                }}
                                                                color="light"
                                                                onClick={() => {
                                                                    handleRemoveControl({
                                                                        framework: controlQuery.frameworkName,
                                                                        control: control
                                                                    });
                                                                }}
                                                            >
                                                                X
                                                            </Badge>
                                                        )}
                                                    </ListGroupItem>
                                                )
                                            })}   
                                        </ListGroup>
                                    ) : (
                                        <p
                                            className="empty-list-message"
                                            style={{
                                                maxHeight: panelExpanded ? "163px" : "43px",
                                            }}
                                        >
                                            {t(EMPTY_LIST)}
                                        </p>
                                    )}

                                    <Row className="mt-2">
                                        <Col sm='12'>
                                            <AsyncReactSelect 
                                                loadOptions={loadControlsOptions}
                                                settings={{
                                                    queryKey: 'title',
                                                    loadOptionsErrorMessage: 'Faild to fetch',
                                                    paramsToInject: {
                                                        controlQuery
                                                    }
                                                }}
                                                value={[]}
                                                onChange={(e) => {
                                                    handleAddControl({
                                                        framework: controlQuery.frameworkName,
                                                        control: e
                                                    })
                                                }}
                                                placeholder={t("Select a control")}
                                                isDisabled={isReadOnly}
                                            />
                                        </Col>
                                    </Row>
                                </div>
                            </div>
                        </div>
                    </Col>
                )
            })}
        </Row>
    );
}

export default withNamespaces()(
    ISMSControls
)